export const DefaultMeta = {
    next: '',
    page: 1,
    total: 0,
    limit: 0,
    previous: '',
}

export interface AxiusResponse {
    data: any;
    status: number;
    message: string;
}

export interface ApiTrigger {
    data: any;
    error: any;
    message: string;
}

export interface StoreInterface {
    _store: {
        _auth: {
            user: User;
            token: string;
        },
        _utils: {
            env: 'test' | 'live';
            limit: number;
            loading: {
                primary: string;
                spinning: boolean;
                secondary: string;
            }
        },
        _data: {
            key: {
                id: string;
                test_key: string;
                live_key: string;
            };
            business: Business;
        }
    },
    _setAuth: (value: { user: User, token: string }) => void;
    _setUser: (value: User) => void;
    _setToken: (value: string) => void;
    _setBusiness: (value: Business) => void;
    _setData: (payload: { key: 'business', value: any }) => void;
    // _setUtils: (payload: { key: 'loading' | 'env', value: any }) => void;
}
export interface User {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
    password: string;
    created_at: string;
    updated_at: string;
    teams: Team[];
    type: UserTypes;
    country?: string;
    status?: TeamStatus;
    admin_role_id?: string;
    admin_role?: AdminRole;
    is_authenticator_enabled: boolean;
}

export enum UserTypes {
    USER = 'USER',
    ADMIN = 'ADMIN',
}

export interface Team {
    id: string;
    userId: string;
    businessId: string;
    business: Business;
    user: User;
    role?: AdminRole;
    roleId?: string;
}

export interface TeamInvite {
    id: string;
    name: string;
    email: string;
    status: TeamStatus;
    businessId: string;
    business: Business;
    created_at: string;
    updated_at: string;
}

export interface AdminRole {
    id: string;
    name: string;
    permissions: string[];
    created_at: string;
    updated_at: string;
}

export interface Business {
    id: string;
    name: string;
    industry: BusinessIndustries;
    description: string
    team_size: BusinessTeamSizes;
    balances: {
        test: number;
        live: number;
    };
    created_at: string;
    updated_at: string;
    callback_url: string;
    failure_callback_url?: string;
    keys: Keys[];
    transactions: Transaction[];
    teams: Team[];
    wallets?: Wallet[];
    ussd_id?: number;
    preferences: {
        fees: {
            GHS: BusinessFee;
            NGN: BusinessFee;
        }
        bear_fee: {
            payout: 'business' | 'customer';
            collection: 'business' | 'customer';
        };
        permissions?: string[];
        mpos_enabled?: boolean;
        ussd_enabled?: boolean;
        source?: 'internal' | 'fast';
        transactions_total?: number;
        transactions_volume?: number;
    };
    status: TeamStatus;
    settlement_accounts?: SettlementAccount[];
    virtual_accounts?: VirtualAccount[];
    compliance: {
        address: {
            zip?: string;
            city: string;
            state: string;
            line1: string;
            line2?: string;
            proof: string;
            country: string;
            proof_link: string;
        },
        directors: Director[],
        shareholders: Shareholder[],
        documents: {
            doc_type: string;
            doc_file: string;
        }[],
        rejections: any;
    };
}

export interface Director {
    first_name: string;
    last_name: string;
    id_type: string;
    id_number: string;
    id_front: string;
    id_back: string;
}

export interface Shareholder {
    type: 'Company' | 'Individual';
    first_name: string;
    last_name: string;
    id_type: string;
    id_number: string;
    id_front: string;
    id_back: string;
    registration_number: string;
    tax_number: string;
    incorporation_date: string;
    country: string;
}

export interface BusinessFee {
    fee_debit_max?: number;
    fee_debit_add?: number;
    fee_debit_percentage?: number;
    fee_credit_max?: number;
    fee_credit_add?: number;
    fee_credit_percentage?: number;
}

export interface Wallet {
    id: string;
    currency: Currencies;
    businessId: string;
    balance: number;
    balance_test: number;
    balance_hold?: number;
    balance_hold_test?: number;
    business?: Business;
}

export enum Currencies {
    GHS = 'GHS',
    NGN = 'NGN',
    UGX = 'UGX',
    XAF = 'XAF',
    XOF = 'XOF',
    KES = 'KES',
    TZS = 'TZS',
    ZMW = 'ZMW',
    GMD = 'GMD',
    CDF = 'CDF',
    GBP = 'GBP',
    EUR = 'EUR',
    USD = 'USD',
    USDT = 'USDT',
    USDC = 'USDC',
}

export const CurrenciesLabels = {
    [Currencies.GHS]: `Ghana Cedi`,
    [Currencies.NGN]: `Naira`,
    [Currencies.UGX]: `Ugandan Shilling`,
    [Currencies.KES]: `Kenyan Shilling`,
    [Currencies.TZS]: `Tanzanian Shilling`,
    [Currencies.ZMW]: `Kwacha`,
    [Currencies.GMD]: `Gambian Dalasi`,
    [Currencies.CDF]: `Congolese Franc`,
    [Currencies.XAF]: `Central African CFA franc`,
    [Currencies.XOF]: `West African CFA franc`,
    [Currencies.GBP]: `Pound`,
    [Currencies.EUR]: `Euro`,
    [Currencies.USD]: `US Dollar`,
    [Currencies.USDT]: `Tether`,
    [Currencies.USDC]: `USD Coin`,
}

export const CurrenciesSymbols = {
    [Currencies.GHS]: '₵',
    [Currencies.NGN]: '₦',
    [Currencies.UGX]: 'Ush',
    [Currencies.KES]: 'Ksh',
    [Currencies.TZS]: 'Tsh',
    [Currencies.ZMW]: 'ZK',
    [Currencies.GMD]: 'D',
    [Currencies.CDF]: 'FC',
    [Currencies.XAF]: 'XAF',
    [Currencies.XOF]: 'XOF',
    [Currencies.EUR]: 'EUR',
    [Currencies.GBP]: 'GBP',
    [Currencies.USD]: 'USD',
    [Currencies.USDT]: 'USDT',
    [Currencies.USDC]: 'USDC',
}

export interface Reversal {
    id: string;
    transactionId: string;
    requesterId: string;
    businessId: string;
    status: 'PENDING' | 'COMPLETED' | 'FAILED';
    comment?: string;
    revTransactionId?: string;
    created_at: string;
}

export interface Sale {
    id: string;
    transactionId: string;
    businessId: string;
    createdById: string;
    createdBy: {
        name: string;
    }
    amount: number;
    currency: Currencies;
    created_at: string;
}

export interface Transaction {
    id: string;
    amount: number;
    currency: Currencies;
    status: TrxStatus;
    channel: TrxChannels;
    type: TrxTypes;
    customer: Customer;
    metadata: any;
    reason: string;
    fee: number;
    provider: TrxProviders;
    provider_init_response: any;
    provider_hook_response: any;
    businessId: string;
    callback_url: string;
    callback_data: any;
    created_at: string;
    updated_at: string;
    keysId: string;
    key: Keys;
    business: Business;
    sales: Sale[];
    reference: string;
    reference_business: string;
    network: string;
    opening_balance: number;
    closing_balance: number;
    bear_fee: 'business' | 'customer';
    provider_message: string;
    reversal?: Reversal;
    chain: TransactionChainNode[];
}

export interface TransactionChainNode {
    id: string;
    type: TrxTypes;
    status: TrxStatus;
    amount: number;
    fee: number;
    currency: Currencies;
    reference: string;
    reference_business: string;
    network: string;
    channel: TrxChannels;
    provider: TrxProviders;
    originalTransactionId: string;
    created_at: string;
    children: TransactionChainNode[];
}

export interface Customer {
    accountName: string;
    accountNumber: string;
}

export interface Recipient {
    amount: number;
    bank?: string;
    network?: string;
    accountName: string;
    accountNumber: string;
}

export interface Keys {
    id: string;
    businessId: string;
    test_key: string;
    live_key: string;
    test_publishable_key?: string | null;
    live_publishable_key?: string | null;
    test_secret_key_generated_at?: string;
    test_secret_key_generated_by?: { id: string; first_name?: string; last_name?: string; email?: string; };
    live_secret_key_generated_at?: string;
    live_secret_key_generated_by?: { id: string; first_name?: string; last_name?: string; email?: string; };
    created_at: string;
    updated_at: string;
    business: Business;
    transactions: Transaction[];
}

export interface IpWhitelist {
    id: string;
    businessId: string;
    ip_address: string;
    description?: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface File {
    lastModified: number;
    lastModifiedDate: Date;
    name: string;
    size: number;
    type: string;
    webkitRelativePath: string;
}

export type UserBusiness = {
    user: User;
    business: Business;
};

export interface Filter {
    key: string;
    type: 'text' | 'number' | 'select' | 'date_range';
    help?: string;
    value?: string;
    label?: string;
    options?: string[] | { label: string; value: string; }[];
    placeholder?: string;
}

export interface DataMeta {
    limit: number;
    next?: string;
    page: number;
    previous?: string;
    total: number;
}

export interface DebitMomo {
    amount: number;
    reason: string;
    network: string;
    currency: string;
    metadata: any;
    accountName: string;
    accountNumber: string;
    callbackURL?: string;
}

export interface CreditMomo {
    amount: number;
    reason: string;
    network: string;
    accountName: string;
    accountNumber: string;
}

export interface CreditBank {
    id?: string;
    amount: number;
    reason: string;
    bank: string;
    senderName: string;
    accountName: string;
    accountNumber: string;
}

export interface TrxSuccessFailure {
    successful: { volume: number; value: number; };
    failed: { volume: number; value: number; };
}

export interface TrxStatusCounts {
    completed: number;
    failed: number;
    pending: number;
}

export interface TrxOverview {
    totalAmount: number;
    totals?: any;
    performance: any;
    totalsCredit: any;
    totalsDebit: any;
    totalVolume?: number;
    totalValue?: number;
    today?: {
        overall: TrxSuccessFailure;
        collections: TrxSuccessFailure;
        payouts: TrxSuccessFailure;
    };
    statusBreakdown?: {
        collections: TrxStatusCounts;
        payouts: TrxStatusCounts;
    };
}

export interface SettlementAccount {
    id: string;
    businessId: string;
    currency: Currencies;
    channel: TrxChannels;
    network: string;
    account_name: string;
    account_number: string;
    is_primary: boolean;
    details: any;
    created_at: string;
    updated_at: string;
    business: Business;
}

export interface VirtualAccount {
    id: string;
    businessId: string;
    currency: Currencies;
    bank_code: string;
    bank_name: string;
    account_name: string;
    account_number: string;
    created_at: string;
    updated_at: string;
    business: Business;
    stage: 'test' | 'live';
    type: 'PERMANENT' | 'TEMPORARY';
    provider_data: any;
    is_active: boolean;
}

export interface PayLink {
    id: string;
    name: string;
    description: string;
    currency: Currencies;
    amount: number | null;
    target: number;
    slug: string;
    redirect: string;
    image: string;
    image_link: string;
    pay_link: string;
    type: PayLinksTypes;
    status: TrxStatus;
    link_status: PayLinkStatus;
    is_used: boolean;
    transactions: Transaction[];
    created_at: string;
    updated_at: string;
    accept_tips: boolean;
    donation_preset?: boolean;
    business: Business;
    fields: CustomField[];
    channels: TrxChannels[];
    preferences: {
        receipt: {
            sms: boolean;
            email: boolean;
        };
        merchantAlert?: {
            email?: { enabled: boolean; userIds: string[] };
            sms?: { enabled: boolean; numbers: string[] };
        };
    };
    schedule_unit?: ScheduleUnit;
    schedule_every?: number;
    schedule_start?: string;
    schedule_time?: string;
    first_sent_at?: string;
    expires_at?: string;
    cadence?: string;
    next_send_at?: string;
    recipients?: PayLinkRecipient[];
}

export interface PayLinkRecipient {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    channels: DeliveryChannel[];
    last_sent_at: string | null;
    paid_count: number;
    opted_out_at: string | null;
    opted_out_by: string | null;
    created_at: string;
    total_volume?: number;
    transactions_count?: number;
}

export interface CustomField {
    name: string;
    input: 'text';
    required: boolean;
}

export enum PayLinksTypes {
    ONETIME = 'ONETIME',
    DONATION = 'DONATION',
    RECURRING = 'RECURRING',
    FIXED = 'FIXED',
    DYNAMIC = 'DYNAMIC',
}

export enum PayLinksTypesLabels {
    ONETIME = 'One-Time Payment',
    DONATION = 'Donation',
    RECURRING = 'Recurring',
    FIXED = 'Fixed Amount',
    DYNAMIC = 'Dynamic',
}

export enum ScheduleUnit {
    DAY = 'DAY',
    WEEK = 'WEEK',
    MONTH = 'MONTH',
}

export enum ScheduleUnitLabels {
    DAY = 'Day(s)',
    WEEK = 'Week(s)',
    MONTH = 'Month(s)',
}

export enum DeliveryChannel {
    EMAIL = 'EMAIL',
    SMS = 'SMS',
}

export enum DeliveryChannelLabels {
    EMAIL = 'Email',
    SMS = 'SMS',
}

export enum DeliveryStatus {
    SENT = 'SENT',
    FAILED = 'FAILED',
}

export enum DeliveryStatusLabels {
    SENT = 'Sent',
    FAILED = 'Failed',
}

export interface PayLinkDelivery {
    id: string;
    paylink_id: string;
    recipient_id: string;
    recipient: {
        id: string;
        name: string;
        email: string | null;
        phone: string | null;
    };
    channel: DeliveryChannel;
    status: DeliveryStatus;
    error: string | null;
    scheduled_for: string;
    created_at: string;
}

export enum PayLinkStatus {
    DRAFT = 'DRAFT',
    ACTIVE = 'ACTIVE',
    USED = 'USED',
    DEACTIVATED = 'DEACTIVATED',
    EXPIRED = 'EXPIRED',
}

export enum PayLinkStatusLabels {
    DRAFT = 'Draft',
    ACTIVE = 'Active',
    USED = 'Used',
    DEACTIVATED = 'Deactivated',
    EXPIRED = 'Expired',
}



export enum TrxStatus {
    INITIATED = 'INITIATED',
    FAILED = 'FAILED',
    COMPLETED = 'COMPLETED',
    OTP = 'OTP',
}

export enum TrxStatusLabels {
    INITIATED = 'Pending',
    FAILED = 'Failed',
    COMPLETED = 'Completed',
    OTP = 'OTP',
    CANCELLED = 'Cancelled',
}

export enum TrxChannels {
    MOMO = 'MOMO',
    BANK = 'BANK',
    CARD = 'CARD',
    CRYPTO = 'CRYPTO',
}

export enum TrxChannelsLabels {
    MOMO = 'Mobile Money',
    BANK = 'Bank',
    CARD = 'Card',
    CRYPTO = 'Crypto',
}

export enum TrxTypes {
    DEBIT = 'DEBIT',
    CREDIT = 'CREDIT',
    SETTLEMENT = 'SETTLEMENT',
}

export enum TrxProviders {
    MTN = 'MTN',
    DOSS = 'DOSS',
    MSAFRICA = 'MSAFRICA',
}

export enum TrxNetworks {
    MTN = 'MTN',
    VOD = 'VOD',
    ATM = 'ATM',
}

export enum NetworksLabels {
    MTN = 'MTN Mobile Money',
    VOD = 'Telecel Cash',
    ATM = 'AirtelTigo Money',
}

export enum BusinessIndustries {
    Agriculture = 'Agriculture',
    Commerce = 'Commerce',
    Education = 'Education',
    Financial = 'Financial',
    Gaming = 'Gaming',
    Hospitality = 'Hospitality',
    Health = 'Health',
    Leisure = 'Leisure',
    Logistics = 'Logistics',
    Non_Profit = 'Non Profit',
    Travel = 'Travel',
    Utilities = 'Utilities',
}

export enum BusinessTeamSizes {
    SMALL = 'Small (1-50)',
    MEDIUM = 'Medium (50-150)',
    LARGE = 'Large (150-250)',
    EXTRA_LARGE = 'Enterprise (250+)',
}

export enum TeamStatus {
    PENDING = 'PENDING',
    ACCEPTED = 'ACCEPTED',
    REJECTED = 'REJECTED',
    ONGOING = 'ONGOING',
    HALTED = 'HALTED',
}

export enum TeamStatusLabels {
    PENDING = 'Pending Approval',
    ACCEPTED = 'Completed',
    REJECTED = 'Rejected',
    ONGOING = 'Under Review',
    HALTED = 'Halted',
}

export enum BusinessStatusLabels {
    PENDING = 'Pending',
    ACCEPTED = 'Approved',
    REJECTED = 'Rejected',
}

export enum Networks {
    MTN = 'MTN',
    VOD = 'VOD',
    ATM = 'ATM',
}

export enum Folders {
    paylinks = 'paylinks',
    compliance = 'compliance',
}

export enum MessageTypes {
    SMS = 'SMS',
    EMAIL = 'EMAIL',
}

export interface MposUser {
    id: string;
    fullName: string;
    phoneNumber: string;
    status: 'INVITED' | 'ACTIVE';
    accountActivatedAt: string | null;
    createdAt: string;
}

export interface MposWorkspace {
    id: string;
    userId: string;
    businessId: string;
    fullName: string;
    phoneNumber: string;
    email?: string;
    status: 'ACTIVE' | 'INVITED' | 'DISABLED';
    lastLoginAt?: string | null;
    createdAt: string;
}

export interface MposTip {
    id: string;
    transactionId: string | null;
    tipAmount: string;
    totalAmount: string;
    createdAt: string;
    workspace?: { fullName: string; phoneNumber: string };
}

export interface MposSettings {
    tipsEnabled: boolean;
    paymentChannels: string[];
}

export const CryptoCurrencies = [Currencies.USDT, Currencies.USDC];
export const VirtualCurrencies = [Currencies.NGN];
export const ActiveCurrencies = Object.keys(Currencies) as Currencies[];
export const ActiveTrxChannels = Object.keys(TrxChannels) as TrxChannels[];
